import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';

import { SubgridEmptyState } from '@/components/SubgridEmptyState';
import { saveStandardTasks } from '@/features/settings/api/tasks';

function parseTasks(value: string | null | undefined): string[] | null {
  if (!value) return [];
  try {
    return z.array(z.string()).parse(JSON.parse(value));
  } catch {
    return null;
  }
}

export function StandardTasksGrid({
  checklist,
  onSaved,
}: {
  checklist: string | null;
  onSaved: (tasks: string[]) => void;
}) {
  const [initial] = useState(() => parseTasks(checklist));
  const [tasks, setTasks] = useState(initial ?? []);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [editor, setEditor] = useState<{ index: number | null; text: string } | null>(null);
  const [message, setMessage] = useState('');
  const save = useMutation({
    mutationFn: (next: string[]) => saveStandardTasks(next),
    onSuccess: (_, next) => {
      setTasks(next);
      setSelected(new Set());
      setEditor(null);
      setMessage('Tasks saved.');
      onSaved(next);
    },
  });
  const selectedIndex = selected.size === 1 ? [...selected][0] : undefined;
  const duplicate =
    editor !== null &&
    tasks.some(
      (task, i) =>
        i !== editor.index && task.trim().toLowerCase() === editor.text.trim().toLowerCase(),
    );
  const openEditor = (index: number | null) => {
    save.reset();
    setMessage('');
    setEditor({ index, text: index === null ? '' : (tasks[index] ?? '') });
  };
  const move = (direction: number) => {
    if (selectedIndex === undefined) return;
    const next = [...tasks];
    const target = selectedIndex + direction;
    const task = next[selectedIndex];
    const other = next[target];
    if (task === undefined || other === undefined) return;
    next[selectedIndex] = other;
    next[target] = task;
    save.mutate(next);
  };
  const toggle = (index: number) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  const blocked = save.isPending || initial === null;
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        boxShadow: '0 2px 6px rgba(0,0,0,.12)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h2">Tasks ({tasks.length})</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          This list appears for each pool on every technician job. With two pools, each task is
          completed twice. New reports use the latest list; reports already started keep their
          existing checklist.
        </Typography>
      </Box>
      <Stack
        direction="row"
        justifyContent="flex-end"
        useFlexGap
        flexWrap="wrap"
        sx={{
          px: 1,
          minHeight: 44,
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button startIcon={<AddIcon />} disabled={blocked} onClick={() => openEditor(null)}>
          Add task
        </Button>
        {selectedIndex !== undefined && (
          <>
            <Button
              startIcon={<EditOutlinedIcon />}
              disabled={blocked}
              onClick={() => openEditor(selectedIndex)}
            >
              Edit
            </Button>
            <Button
              startIcon={<ArrowUpwardIcon />}
              disabled={blocked || selectedIndex === 0}
              onClick={() => move(-1)}
            >
              Move up
            </Button>
            <Button
              startIcon={<ArrowDownwardIcon />}
              disabled={blocked || selectedIndex === tasks.length - 1}
              onClick={() => move(1)}
            >
              Move down
            </Button>
          </>
        )}
        {selected.size > 0 && (
          <Button
            startIcon={<DeleteOutlineIcon />}
            disabled={blocked}
            onClick={() => save.mutate(tasks.filter((_, i) => !selected.has(i)))}
          >
            Remove
          </Button>
        )}
      </Stack>
      {initial === null && (
        <Alert severity="error">
          The saved tasks could not be read. Refresh the page before trying again.
        </Alert>
      )}
      {save.isError && (
        <Alert severity="error">
          Tasks could not be saved. Your existing tasks are unchanged; try again.
        </Alert>
      )}
      {message && (
        <Typography role="status" color="success.main" sx={{ px: 2, pt: 1 }}>
          {message}
        </Typography>
      )}
      <TableContainer>
        <Table
          size="small"
          aria-label="Standard pool tasks"
          sx={{ '& .MuiTableCell-root': { height: 48, borderBottom: '1px solid #edebe9' } }}
        >
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  disabled={blocked || !tasks.length}
                  checked={tasks.length > 0 && selected.size === tasks.length}
                  indeterminate={selected.size > 0 && selected.size < tasks.length}
                  inputProps={{ 'aria-label': 'Select all tasks' }}
                  onChange={() =>
                    setSelected(
                      selected.size === tasks.length ? new Set() : new Set(tasks.map((_, i) => i)),
                    )
                  }
                />
              </TableCell>
              <TableCell sx={{ width: 70, fontWeight: 600 }}>Order</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Task</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((task, index) => (
              <TableRow key={`${index}-${task}`} hover selected={selected.has(index)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    disabled={blocked}
                    checked={selected.has(index)}
                    onChange={() => toggle(index)}
                    inputProps={{ 'aria-label': `Select task ${index + 1}: ${task}` }}
                  />
                </TableCell>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <Link
                    component="button"
                    disabled={blocked}
                    onClick={() => openEditor(index)}
                    sx={{ textAlign: 'left', overflowWrap: 'anywhere' }}
                  >
                    {task}
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!tasks.length && initial !== null && (
        <SubgridEmptyState message="No tasks yet. Choose Add task to create one." />
      )}
      <Typography variant="body2" sx={{ p: 1.5 }}>
        Rows: {tasks.length}
        {selected.size > 0 ? ` · Selected: ${selected.size}` : ''}
      </Typography>
      <Dialog
        open={editor !== null}
        fullWidth
        maxWidth="sm"
        onClose={save.isPending ? undefined : () => setEditor(null)}
        aria-labelledby="task-title"
      >
        <DialogTitle id="task-title">
          {editor?.index === null ? 'Add task' : 'Edit task'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Task instruction"
            value={editor?.text ?? ''}
            disabled={save.isPending}
            onChange={(event) => setEditor(editor ? { ...editor, text: event.target.value } : null)}
            error={duplicate}
            helperText={
              duplicate
                ? 'This task is already in the list.'
                : 'Describe what the technician needs to do.'
            }
            sx={{ mt: 1 }}
          />
          {save.isError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              The task could not be saved. Your instruction is still here; try again.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={save.isPending} onClick={() => setEditor(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={save.isPending || !editor?.text.trim() || duplicate}
            onClick={() => {
              if (!editor) return;
              const next = [...tasks];
              if (editor.index === null) next.push(editor.text.trim());
              else next[editor.index] = editor.text.trim();
              save.mutate(next);
            }}
          >
            {save.isPending ? 'Saving…' : 'Save task'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
